import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Paper } from '@mui/material';
import styles from './TAIChat.module.css';
import { ChatHeader } from './ChatHeader';
import { MessageList, ChatMessage } from './MessageList';
import { InputBar } from './components/InputBar';
import { buildTagSummarizationQuery, buildDisplayText, buildMediaCdnUrl, buildRequestBody, AI_ENDPOINTS, handleTAIResponse } from './helpers';
import { getCameraBoundingBox } from '../../../../utils/cameraPositions';
import lowercaseExtension from '../../../../utils/lowercaseExtension';
import { captureShowcaseScreenshot, validateScreenshotSize, getContentTypeFromDataUrl, collectMediaMetadata } from './helpers/screenshotUtils';
import { selectImageFile, validateAndConvertImage } from './helpers/imageUploadUtils';
import { AreaSelector } from './components/AreaSelector';
import { useTAIChat } from './hooks/useTAIChat';
import { useTAIConversations } from './hooks/useTAIConversations';
import { useNavigationAgent } from './hooks/useNavigationAgent';
import { useFingerprint } from './hooks/useFingerprint';
import { AIMessageRole } from './enum';
import { useTAIChatStore } from '../../../../store';
import { ConversationHistory } from './ConversationHistory';
// import stripHtmlTags from '../../../../utils/stripHtmlTags';

export function stripHtmlTags (str: string) {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '');
};

const CDN_BASE_URL = process.env.NEXT_PUBLIC_CDN || 'https://stage-cdn.staging.treedis.com';

export enum AIRequestType {
  GeneralQuery = 'generalQuery',
  TextSummarize = 'textSummarize',
  PdfSummary = 'pdfSummary',
  ImageAnalysis = 'imageAnalysis',
}

export function ChatWindow({ tourSlug, tourId, tourTitle, onClose, moveToTag, tags, showcase }: {
  tourSlug: string;
  tourId: number;
  tourTitle: string;
  onClose: () => void;
  moveToTag?: (sid: string, doOpenTag?: boolean) => void;
  tags?: any[];
  showcase?: any;
}) {

  const currentConversationId = useTAIChatStore((state) => state.currentConversationId);
  const storedSessionId = useTAIChatStore((state) => state.sessionId);
  const messages = useTAIChatStore((state) => state.messages);
  const conversations = useTAIChatStore((state) => state.conversations);
  const conversationsLoading = useTAIChatStore((state) => state.conversationsLoading);
  const setCurrentConversationId = useTAIChatStore((state) => state.setCurrentConversationId);
  const setSessionId = useTAIChatStore((state) => state.setSessionId);
  const setMessages = useTAIChatStore((state) => state.setMessages);
  const addConversation = useTAIChatStore((state) => state.addConversation);
  const resetChat = useTAIChatStore((state) => state.resetChat);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSelectingArea, setIsSelectingArea] = useState(false);
  const [fullScreenshot, setFullScreenshot] = useState<string>('');
  const [pendingImage, setPendingImage] = useState<string>('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const { createTAIConversation, sendTAIMessage } = useTAIChat();
  const { fetchConversations, loadConversation } = useTAIConversations();
  const { handleNavigationAgentResponse } = useNavigationAgent({
    showcase,
    tags,
  });
  const { fingerprint } = useFingerprint();

  const sessionId = useMemo(() => {
    if (storedSessionId) {
      return storedSessionId;
    }
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    return newSessionId;
  }, [storedSessionId, setSessionId]);

  const getCurrentPosition = useCallback(async () => {
    try {
      if (showcase?.Camera?.getPose) {
        const pose = await showcase.Camera.getPose();
        return pose.position;
      }
      return null;
    } catch (error) {
      console.error('Failed to get camera position:', error);
      return null;
    }
  }, [showcase]);

  const getCameraCorners = useCallback(async () => {
    try {
      const cameraBoundingBox = await getCameraBoundingBox(showcase);
      return cameraBoundingBox?.corners || [];
    } catch (err) {
      console.error('Error getting camera corners:', err);
      return [];
    }
  }, [showcase]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }));
  }, []);

  useEffect(() => {
    if (tourId) {
      fetchConversations(tourId);
    }
  }, [tourId, fetchConversations]);

  // // Expose camera and tag functions to console for debugging
  // useEffect(() => {
  //   if (showcase) {
  //     (window as any).getCameraBoundingBox = () => getCameraBoundingBox(showcase);
  //     (window as any).getVisibleTags = getVisibleTagsInView;
  //     (window as any).getAllTags = () => getAllTags(showcase);
  //   }
  //   return () => {
  //     delete (window as any).getCameraBoundingBox;
  //     delete (window as any).getVisibleTags;
  //     delete (window as any).getAllTags;
  //   };
  // }, [showcase, getVisibleTagsInView]);

  const sendWithImage = useCallback(async () => {
    if (!pendingImage) return;

    const userQuery = input.trim();
    if (!userQuery || loading) return;

    const croppedImage = pendingImage;
    setPendingImage('');
    setInput('');
    setLoading(true);

    const userMsg: ChatMessage = {
      id: `${Date.now()}-u`,
      role: AIMessageRole.User,
      text: userQuery,
      raw: {
        screenshot: croppedImage,
        isScreenshotQuery: true
      }
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    scrollToBottom();

    try {
      const mediaMetadata = collectMediaMetadata(croppedImage);
      let imageUrl = croppedImage;
      let conversationId = currentConversationId;

      if (!conversationId) {
        const conversationResponse = await createTAIConversation({
          tourSlug,
          tourId,
          sessionId,
          message: {
            role: AIMessageRole.User,
            content: userQuery,
            media: [mediaMetadata],
          },
        });

        if (!conversationResponse) {
          throw new Error('Failed to create conversation');
        }

        conversationId = conversationResponse.id;
        setCurrentConversationId(conversationResponse.id);
        // Add the new conversation to the history
        addConversation({
          id: conversationResponse.id,
          tourId: conversationResponse.tourId,
          sessionUuid: conversationResponse.sessionUuid,
          fingerprint: conversationResponse.fingerprint,
          title: conversationResponse.title,
          lastMessageAt: conversationResponse.lastMessageAt,
          createdAt: conversationResponse.createdAt,
          updatedAt: conversationResponse.updatedAt,
        });

        const lastMessage = conversationResponse.messages[conversationResponse.messages.length - 1];
        const mediaAttachment = lastMessage?.mediaAttachments?.[0];

        if (mediaAttachment?.presignedUrl) {
          const uploadResponse = await fetch(mediaAttachment.presignedUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': mediaMetadata.contentType,
            },
            body: mediaMetadata.blob,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload media: ${uploadResponse.status}`);
          }

          if (mediaAttachment.downloadUrl) {
            imageUrl = mediaAttachment.downloadUrl;
          }
        }
      } else {
        const messageResponse = await sendTAIMessage(conversationId, {
          role: AIMessageRole.User,
          content: userQuery,
          media: [mediaMetadata],
        });

        if (messageResponse?.mediaAttachments?.[0]?.presignedUrl) {
          const uploadResponse = await fetch(messageResponse.mediaAttachments[0].presignedUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': mediaMetadata.contentType,
            },
            body: mediaMetadata.blob,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload media: ${uploadResponse.status}`);
          }

          if (messageResponse.mediaAttachments[0].downloadUrl) {
            imageUrl = messageResponse.mediaAttachments[0].downloadUrl;
          }
        }
      }

      const position = await getCurrentPosition();
      const cameraCorners = await getCameraCorners();

      const requestBody = {
        url: imageUrl,
        userQuery: userQuery,
        tourId: tourId,
        tourSlug: tourSlug,
        sessionId: sessionId,
        language: 'English',
        position,
        cameraCorners,
        userId: fingerprint || undefined,
      };

      const aiResponse = await fetch(AI_ENDPOINTS.analyzeImage, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const responseData = await aiResponse.json();

      if (responseData.error || !responseData.success) {
        throw new Error(responseData.error || 'Image analysis failed');
      }

      // Handle different agent response formats
      const aiResponseText = await handleTAIResponse({
        data: responseData,
        handleNavigationAgentResponse,
      });

      if (conversationId) {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.Bot,
          content: aiResponseText,
          media: [],
        });
      }

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: AIMessageRole.Bot,
        text: aiResponseText,
        raw: {
          ...responseData,
          isImageAnalysis: true,
          screenshot: croppedImage,
        },
        query: null,
      };

      setMessages([...updatedMessages, assistantMsg]);
    } catch (err: any) {
      console.error('Screenshot analysis error:', err);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: AIMessageRole.Bot,
        text: `Error: ${err?.message || 'Failed to analyze screenshot'}`
      };
      setMessages([...updatedMessages, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [pendingImage, input, loading, currentConversationId, tourId, sessionId, scrollToBottom, getCurrentPosition, getCameraCorners]);

  const send = useCallback(async () => {
    if (pendingImage) {
      return sendWithImage();
    }

    const userQuery = input.trim();
    if (!userQuery || loading) return;

    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: AIMessageRole.User, text: userQuery };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      let conversationId = currentConversationId;

      if (!conversationId) {
        const response = await createTAIConversation({
          tourSlug,
          tourId,
          sessionId,
          message: {
            role: AIMessageRole.User,
            content: userQuery,
            media: [],
          },
        });
        if (response && response.id) {
          conversationId = response.id;
          setCurrentConversationId(response.id);
          // Add the new conversation to the history
          addConversation({
            id: response.id,
            tourId: response.tourId,
            sessionUuid: response.sessionUuid,
            fingerprint: response.fingerprint,
            title: response.title,
            lastMessageAt: response.lastMessageAt,
            createdAt: response.createdAt,
            updatedAt: response.updatedAt,
          });
        }
      } else {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.User,
          content: userQuery,
          media: [],
        });
      }

      const position = await getCurrentPosition();
      const cameraCorners = await getCameraCorners();

      const requestQuery = `${userQuery}`;
      const res = await fetch(AI_ENDPOINTS.query, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userQuery: requestQuery,
          sessionId,
          tourId,
          tourSlug,
          position,
          cameraCorners,
          userId: fingerprint || undefined,
        }),
      });
      const data = await res.json();

      // Handle different agent response formats
      const aiResponseText = await handleTAIResponse({
        data,
        handleNavigationAgentResponse,
      });

      if (conversationId) {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.Bot,
          content: aiResponseText,
          media: [],
        });
      }

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: AIMessageRole.Bot,
        text: aiResponseText,
        raw: data,
        query: typeof data?.query === 'string' ? data.query : null,
      };
      setMessages([...updatedMessages, assistantMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = { id: `${Date.now()}-e`, role: AIMessageRole.Bot, text: `Error: ${err?.message || 'Something went wrong. Please try again.'}` };
      setMessages([...updatedMessages, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [input, loading, pendingImage, sendWithImage, currentConversationId, tourId, sessionId, scrollToBottom, getCurrentPosition, getCameraCorners]);

  const checkTagHasPdf = useCallback((tagId: string): boolean => {
    if (!tags || tags.length === 0) {
      return false;
    }

    const tag = tags.find((t: any) =>
      String(t.id) === String(tagId) ||
      String(t.sid) === String(tagId) ||
      String(t.defaultSid) === String(tagId)
    );

    if (!tag) {
      return false;
    }

    if (!tag.tagGalleries || !Array.isArray(tag.tagGalleries) || tag.tagGalleries.length === 0) {
      return false;
    }

    const hasPdf = tag.tagGalleries.some((gallery: any) => {
      const url = gallery?.url;
      return url && typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
    });

    return hasPdf;
  }, [tags]);

  const handleAIRequest = useCallback(async (type: AIRequestType, data: any, displayText: string, query: string) => {
    if (loading) return;

    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: AIMessageRole.User, text: displayText };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      let conversationId = currentConversationId;

      // Save user message to backend
      if (!conversationId) {
        const response = await createTAIConversation({
          tourSlug,
          tourId,
          sessionId,
          message: {
            role: AIMessageRole.User,
            content: displayText,
            media: [],
          },
        });
        if (response && response.id) {
          conversationId = response.id;
          setCurrentConversationId(response.id);
          // Add the new conversation to the history
          addConversation({
            id: response.id,
            tourId: response.tourId,
            sessionUuid: response.sessionUuid,
            fingerprint: response.fingerprint,
            title: response.title,
            lastMessageAt: response.lastMessageAt,
            createdAt: response.createdAt,
            updatedAt: response.updatedAt,
          });
        }
      } else {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.User,
          content: displayText,
          media: [],
        });
      }

      const position = await getCurrentPosition();
      const cameraCorners = await getCameraCorners();
      let response: Response;

      response = await buildRequestBody({
        type,
        data: {
          ...data,
          position,
          cameraCorners
        },
        query,
        fingerprint: fingerprint || undefined,
      });

      const responseData = await response.json();

      // Handle different agent response formats
      const aiResponseText = await handleTAIResponse({
        data: responseData,
        handleNavigationAgentResponse,
      });

      // Save AI response to backend
      if (conversationId) {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.Bot,
          content: aiResponseText,
          media: [],
        });
      }

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: AIMessageRole.Bot,
        text: aiResponseText,
        raw: {
          ...responseData,
          ...(type === AIRequestType.GeneralQuery && query.toLowerCase().includes('pdf') && data.pdfUrl && {
            pdfUrl: data.pdfUrl,
            isPdfSummary: true,
            tagId: data.tagId,
            tagTitle: data.tagTitle
          })
        },
        query: typeof responseData?.query === 'string' ? responseData.query : null,
      };
      setMessages([...updatedMessages, assistantMsg]);
    } catch (err: any) {
      console.error(`Error in ${type} request:`, err);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: AIMessageRole.Bot,
        text: `Error: ${err?.message || 'Something went wrong. Please try again.'}`
      };
      setMessages([...updatedMessages, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [loading, sessionId, tourId, currentConversationId, messages, scrollToBottom, getCurrentPosition, getCameraCorners]);

  const handleScreenshotAndAnalyze = useCallback(async (displayText: string, query: string, data: any) => {
    if (loading) return;
    if (!showcase) {
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: AIMessageRole.Bot,
        text: 'Error: Showcase not available for screenshot capture'
      };
      setMessages([...messages, errorMsg]);
      return;
    }

    try {
      const screenshot = await captureShowcaseScreenshot(showcase, {
        width: 1920,
        height: 1080,
        mattertags: true,
        sweeps: false,
      });

      validateScreenshotSize(screenshot.dataUrl, 5);

      setPendingImage(screenshot.dataUrl);
      setInput(displayText);
      scrollToBottom();
    } catch (error) {
      console.error('Screenshot capture error:', error);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-error`,
        role: AIMessageRole.Bot,
        text: 'Failed to capture screenshot. Please try again.',
      };
      setMessages([...messages, errorMsg]);
    }
  }, [loading, showcase, messages, setMessages, scrollToBottom]);

  const handleStartAreaSelection = useCallback(async (displayText: string, query: string, data: any) => {
    if (loading) return;

    try {
      if (!showcase) {
        console.error('Showcase not available');
        return;
      }

      const screenshot = await captureShowcaseScreenshot(showcase, {
        width: 1920,
        height: 1080,
        mattertags: true,
        sweeps: false,
      });

      validateScreenshotSize(screenshot.dataUrl, 5);

      setFullScreenshot(screenshot.dataUrl);
      setIsSelectingArea(true);

      (window as any)._pendingScreenshotQuery = { displayText, query, data };
    } catch (error) {
      console.error('Screenshot capture error:', error);
      setMessages([
        ...messages,
        {
          id: `${Date.now()}-error`,
          role: AIMessageRole.Bot,
          text: 'Failed to capture screenshot. Please try again.',
        },
      ]);
    }
  }, [loading, showcase, messages, setMessages]);

  const handleAreaConfirm = useCallback(async (croppedImage: string) => {
    setIsSelectingArea(false);
    setFullScreenshot('');

    setPendingImage(croppedImage);
    setInput('Analyze the image');
    scrollToBottom();
  }, [scrollToBottom]);

  const handleAreaCancel = useCallback(() => {
    setIsSelectingArea(false);
    setFullScreenshot('');
    delete (window as any)._pendingScreenshotQuery;
  }, []);

  const handleUploadImage = useCallback(async () => {
    if (loading) return;

    try {
      const file = await selectImageFile();

      if (!file) {
        return;
      }

      const result = await validateAndConvertImage(file, 20);

      if (!result.success) {
        const errorMsg: ChatMessage = {
          id: `${Date.now()}-error`,
          role: AIMessageRole.Bot,
          text: 'error' in result ? result.error : 'Failed to process image',
        };
        setMessages([...messages, errorMsg]);
        return;
      }

      setPendingImage('dataUrl' in result ? result.dataUrl : '');
      setInput('Analyze the image');
      scrollToBottom();
    } catch (error) {
      console.error('Image upload error:', error);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-error`,
        role: AIMessageRole.Bot,
        text: 'Failed to upload image. Please try again.',
      };
      setMessages([...messages, errorMsg]);
    }
  }, [loading, messages, setMessages, scrollToBottom]);

  const handleSummarizePdf = useCallback(async (tagId: string, tagTitle?: string) => {
    if (loading) return;

    const displayText = `Summarize tag ${tagTitle || tagId} PDF file`;

    const tag = tags?.find((t: any) =>
      String(t.sid) === String(tagId) ||
      String(t.id) === String(tagId) ||
      String(t.defaultSid) === String(tagId)
    );

    let pdfUrl = tag?.tagGalleries?.find((gallery: any) => {
      const url = gallery?.url;
      return url && typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
    })?.url;

    if (!pdfUrl) {
      console.error('No PDF URL found for tag:', tagId);
      return;
    }

    pdfUrl = lowercaseExtension(buildMediaCdnUrl(pdfUrl, CDN_BASE_URL));

    if (!pdfUrl.startsWith('http://') && !pdfUrl.startsWith('https://')) {
      console.error('Invalid PDF URL format:', pdfUrl);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: AIMessageRole.Bot,
        text: 'Error: PDF URL must start with http:// or https://'
      };
      setMessages([...messages, errorMsg]);
      return;
    }

    if (!pdfUrl.toLowerCase().endsWith('.pdf')) {
      console.error('URL does not point to a PDF file:', pdfUrl);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: AIMessageRole.Bot,
        text: 'Error: URL must point to a PDF file'
      };
      setMessages([...messages, errorMsg]);
      return;
    }

    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: AIMessageRole.User, text: displayText };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      let conversationId = currentConversationId;

      if (!conversationId) {
        const response = await createTAIConversation({
          tourSlug,
          tourId,
          sessionId,
          message: {
            role: AIMessageRole.User,
            content: displayText,
            media: [],
          },
        });
        if (response && response.id) {
          conversationId = response.id;
          setCurrentConversationId(response.id);
          // Add the new conversation to the history
          addConversation({
            id: response.id,
            tourId: response.tourId,
            sessionUuid: response.sessionUuid,
            fingerprint: response.fingerprint,
            title: response.title,
            lastMessageAt: response.lastMessageAt,
            createdAt: response.createdAt,
            updatedAt: response.updatedAt,
          });
        }
      } else {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.User,
          content: displayText,
          media: [],
        });
      }

      const position = await getCurrentPosition();
      const cameraCorners = await getCameraCorners();

      const requestBody = {
        url: pdfUrl,
        tourId,
        tourSlug,
        sessionId,
        maxLength: 500,
        language: 'English',
        position,
        cameraCorners,
        userId: fingerprint || undefined,
      };

      const res = await fetch(AI_ENDPOINTS.summarizePdf, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();

      if (data.error || !data.success) {
        throw new Error(data.error || 'PDF summarization failed');
      }

      // Handle different agent response formats
      const aiResponseText = await handleTAIResponse({
        data,
        handleNavigationAgentResponse,
      });

      if (conversationId) {
        await sendTAIMessage(conversationId, {
          role: AIMessageRole.Bot,
          content: aiResponseText,
          media: [],
        });
      }

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: AIMessageRole.Bot,
        text: aiResponseText,
        raw: {
          ...data,
          pdfUrl,
          isPdfSummary: true,
          tagId,
          tagTitle
        },
        query: null,
      };
      setMessages([...updatedMessages, assistantMsg]);
    } catch (err: any) {
      console.error('Error in PDF summarization:', err);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: AIMessageRole.Bot,
        text: `Error: ${err?.message || 'Failed to summarize PDF'}`
      };
      setMessages([...updatedMessages, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [loading, sessionId, tourSlug, tourId, tags, currentConversationId, messages, scrollToBottom, getCurrentPosition, getCameraCorners]);

  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMsg: ChatMessage = {
        id: 'welcome-msg',
        role: AIMessageRole.Bot,
        text: `Hi! You’re in ${tourTitle}. Want a quick overview?`,
        raw: {
          isWelcome: true,
          suggestions: [
            {
              label: "What I'm looking at",
              query: "What am I looking at?",
              type: AIRequestType.ImageAnalysis,
              data: { tourId, tourSlug, sessionId },
              requiresScreenshot: true
            },
            {
              label: "List 10 closest tags",
              query: "List 10 closest tags",
              type: AIRequestType.GeneralQuery,
              data: { tourId, tourSlug, sessionId }
            },
            {
              label: "Show the most recent tag",
              query: "Show the most recent tag",
              type: AIRequestType.GeneralQuery,
              data: { tourId, tourSlug, sessionId }
            },
            {
              label: "Tell me about this tour",
              query: "Tell me about this tour",
              type: AIRequestType.GeneralQuery,
              data: { tourId, tourSlug, sessionId }
            }
          ]
        }
      };
      setMessages([welcomeMsg]);
    }
  }, [messages.length, tourId, tourSlug, sessionId]);

  const handleSuggestionClick = useCallback(async (suggestion: any) => {
    if (loading) return;

    const displayText = suggestion.displayText || suggestion.query;
    let modifiedQuery = suggestion.query;
    let modifiedData = suggestion.data;

    const position = await getCurrentPosition();

    if (suggestion.label === "List 10 closest tags" && showcase && showcase.Camera) {
      try {
        const currentPose = await showcase.Camera.getPose();

        modifiedQuery = `Find 10 tags within 10 meters radius around my current position. My position is x=${currentPose.position.x.toFixed(2)}, y=${currentPose.position.y.toFixed(2)}, z=${currentPose.position.z.toFixed(2)}, `;

        modifiedData = {
          ...suggestion.data,
          cameraPosition: currentPose.position,
          radius: 10,
          maxTags: 10,
          position,
          tourId,
          tourSlug,
          sessionId
        };
      } catch (err) {
        console.warn('Could not get camera pose for tag search:', err);
      }
    } else {
      modifiedData = {
        ...suggestion.data,
        position,
        tourId,
        tourSlug,
        sessionId
      };
    }

    if (suggestion.requiresAreaSelection && suggestion.type === AIRequestType.ImageAnalysis) {
      await handleStartAreaSelection(displayText, modifiedQuery, modifiedData);
    }
    else if (suggestion.requiresScreenshot && suggestion.type === AIRequestType.ImageAnalysis) {
      await handleScreenshotAndAnalyze(displayText, modifiedQuery, modifiedData);
    } else {
      handleAIRequest(
        suggestion.type,
        modifiedData,
        displayText,
        modifiedQuery
      );
    }
  }, [loading, handleAIRequest, handleScreenshotAndAnalyze, handleStartAreaSelection, showcase, getCurrentPosition]);

  const handleSelectConversation = useCallback(async (conversationId: number) => {
    await loadConversation(conversationId);
    scrollToBottom();
  }, [loadConversation, scrollToBottom]);

  const handleNewChat = useCallback(() => {
    resetChat();
    scrollToBottom();
  }, [resetChat, scrollToBottom]);

  const handleTagNavigation = useCallback((sid: string) => {
    moveToTag?.(sid, false);

    const tag = tags?.find((t: any) =>
      String(t.sid) === String(sid) ||
      String(t.id) === String(sid) ||
      String(t.defaultSid) === String(sid)
    );
    const tagTitle = tag?.title || `Tag ${sid}`;

    const strippedDescription = stripHtmlTags(tag?.description);

    const hasDescription = strippedDescription && strippedDescription.trim().length > 0;
    const hasTagGalleries = tag?.tagGalleries && Array.isArray(tag.tagGalleries) && tag.tagGalleries.length > 0;

    if (!hasDescription && !hasTagGalleries) {
      const simpleInfoMsg: ChatMessage = {
        id: `tag-nav-${Date.now()}`,
        role: AIMessageRole.Bot,
        text: `Navigated to ${tagTitle}. Tag has no description and no media`,
        raw: {
          isTagNavigation: true,
          isSimpleTag: true
        }
      };
      setMessages([...messages, simpleInfoMsg]);
      scrollToBottom();
      return;
    }

    const tagInfo: string[] = [];
    if (tag?.title) tagInfo.push(`Title: ${tag.title}`);
    if (tag?.icon) tagInfo.push(`Icon: ${tag.icon}`);
    if (hasDescription) tagInfo.push(`Description: ${tag.description}`);


    if (hasTagGalleries) {
      const galleryInfo = tag.tagGalleries.map((gallery: any, idx: number) => {
        const parts = [];

        if (gallery.url) {
          const mediaUrl = lowercaseExtension(buildMediaCdnUrl(gallery.url, CDN_BASE_URL));
          parts.push(`URL: ${mediaUrl}`);
        }
        if (gallery.type) parts.push(`Type: ${gallery.type}`);
        if (gallery.title) parts.push(`Title: ${gallery.title}`);
        if (gallery.description) parts.push(`Description: ${gallery.description}`);
        return `Media ${idx + 1}: ${parts.join(', ')}`;
      }).join('\n');
      tagInfo.push(`Media:\n${galleryInfo}`);
    }

    const tagText = tagInfo.length > 0
      ? tagInfo.join('\n\n')
      : `Tag: ${tagTitle}`;

    const pdfGallery = tag?.tagGalleries?.find((gallery: any) => {
      const url = gallery?.url;
      return url && typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
    });
    const hasPdf = !!pdfGallery;
    const pdfUrl = pdfGallery ? lowercaseExtension(buildMediaCdnUrl(pdfGallery.url, CDN_BASE_URL)) : null;

    const query = buildTagSummarizationQuery({
      tagTitle,
      hasIcon: !!tag?.icon,
      hasMedia: hasTagGalleries,
      hasDescription
    });

    const suggestionMsg: ChatMessage = {
      id: `tag-nav-${Date.now()}`,
      role: AIMessageRole.Bot,
      text: `Navigated to ${tagTitle}`,
      raw: {
        isTagNavigation: true,
        hasPdf,
        pdfUrl,
        tagId: sid,
        tagTitle,
        tagSuggestion: {
          label: "Info about tag",
          query,
          displayText: buildDisplayText(tagTitle),
          type: AIRequestType.TextSummarize,
          data: {
            text: tagText,
            query,
            tagId: sid,
            tagTitle,
            sessionId,
            maxLength: 500,
            language: 'English'
          }
        }
      }
    };

    setMessages([...messages, suggestionMsg]);
    scrollToBottom();
  }, [moveToTag, tags, sessionId, tourSlug, scrollToBottom, messages, setMessages, tourId]);

  const currentConversation = conversations.find(c => c.id === currentConversationId);
  const chatTitle = currentConversation?.title || (currentConversationId ? 'Chat' : 'New chat');

  return (
    <>
    <ConversationHistory
      open={historyOpen}
      onClose={() => setHistoryOpen(false)}
      conversations={conversations}
      loading={conversationsLoading}
      currentConversationId={currentConversationId}
      onSelectConversation={handleSelectConversation}
      onNewChat={handleNewChat}
    />
    <Paper className={styles.container} elevation={4} sx={{ background: '#FFF', borderRadius: 4 }}>
      <ChatHeader
        onClose={onClose}
        onMenuClick={() => setHistoryOpen(true)}
        title={chatTitle}
      />
      <div ref={listRef} className={styles.messages}>
        <MessageList
          messages={messages}
          loading={loading}
          onClickTag={handleTagNavigation}
          checkTagHasPdf={checkTagHasPdf}
          onSummarizePdf={handleSummarizePdf}
          onSuggestionClick={handleSuggestionClick}
        />
      </div>
      <div className={styles.inputBar}>
        <InputBar
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onSubmit={send}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
          loading={loading}
          onFullScreenshot={() => handleScreenshotAndAnalyze(
            "What I'm looking at",
            "What am I looking at?",
            { tourId, sessionId }
          )}
          onAreaScreenshot={() => handleStartAreaSelection(
            "Analyze selected area",
            "What is in this area?",
            { tourId, sessionId }
          )}
          onUploadImage={handleUploadImage}
          pendingImage={pendingImage}
          onRemoveImage={() => {
            setPendingImage('');
            setInput('');
          }}
        />
      </div>
      <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }} />
      <div className={styles.footerNote} style={{ padding: '8px 12px' }}>
        AI-powered chat. Verify important info.
      </div>
    </Paper>
    {isSelectingArea && fullScreenshot && (
      <AreaSelector
        screenshot={fullScreenshot}
        onConfirm={handleAreaConfirm}
        onCancel={handleAreaCancel}
      />
    )}
  </>
  );
}

